import { FC, useEffect } from "react";
import { Box } from "@mui/material";
import Flèche from "../../assets/Flèche.gif";
import { MODAL_STEPS } from "../../constants/modal";
import { useTranslation } from "react-i18next";
import { findBrandByDomain } from "../../configs/configLoader";

import "./index.css";
import { activeStylesContinue } from "../../configs";
import { trackScanFlow } from "../../utils/flowTracking";
interface IPropsCamera {
  step: any;
  photo: any | null;
  handleSendImage: () => Promise<void>;
  nextStep: () => void;
  handleOrientation: (e: DeviceOrientationEvent) => void;
  isReady: boolean;
}

const Camera: FC<IPropsCamera> = ({
  step,
  photo,
  handleSendImage,
  nextStep,
  handleOrientation,
  isReady,
}) => {
  const { t } = useTranslation("components/intro");

  const brandDefined = findBrandByDomain();

  const isIntersport = brandDefined?.name === "Intersport";

  useEffect(() => {
    const handleData = async () => {
      if (step?.number === MODAL_STEPS.SCAN_2.number && isReady && photo) {
        handleSendImage();
      }
    };

    handleData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isReady, photo]);

  const requestPermission = async () => {
    if (
      typeof DeviceMotionEvent !== "undefined" &&
      (DeviceMotionEvent as any).requestPermission
    ) {
      try {
        const response = await (DeviceMotionEvent as any).requestPermission();
        trackScanFlow("motion_permission", { step: "SCAN_1", value: response });
        if (response === "granted") {
          setupOrientationListener();
        }
      } catch (err) {
        console.error("Permission request failed:", err);
        trackScanFlow("motion_permission", {
          step: "SCAN_1",
          value: "error",
          reason: (err as Error)?.name || String(err),
        });
      }
    } else {
      trackScanFlow("motion_permission", {
        step: "SCAN_1",
        value: "not_required",
      });
      setupOrientationListener();
    }
  };

  const setupOrientationListener = () => {
    let orientationDetected = false;

    const testOrientation = (e: DeviceOrientationEvent) => {
      if (e.beta !== null) {
        orientationDetected = true;
        window.removeEventListener("deviceorientation", testOrientation);
        window.addEventListener("deviceorientation", handleOrientation);
        console.log("Using DeviceOrientationEvent");
        trackScanFlow("orientation_source", { value: "deviceorientation" });
      }
    };

    window.addEventListener("deviceorientation", testOrientation);

    setTimeout(() => {
      if (!orientationDetected) {
        console.warn(
          "DeviceOrientationEvent did not fire, trying DeviceMotionEvent..."
        );
        window.removeEventListener("deviceorientation", testOrientation);
        window.addEventListener("devicemotion", handleMotion);

        // Neither sensor reports: the level bubble never moves and the photo
        // is never taken.
        let motionDetected = false;
        const testMotion = (e: DeviceMotionEvent) => {
          if (e.accelerationIncludingGravity) motionDetected = true;
          window.removeEventListener("devicemotion", testMotion);
        };
        window.addEventListener("devicemotion", testMotion);
        setTimeout(() => {
          window.removeEventListener("devicemotion", testMotion);
          trackScanFlow(
            motionDetected ? "orientation_source" : "orientation_unavailable",
            { value: motionDetected ? "devicemotion" : "none" }
          );
        }, 2000);
      }
    }, 1000);
  };

  // Keep a history of beta and gamma values for smoothing
  let motionGammaHistory: number[] = [];
  let motionBetaHistory: number[] = [];

  const handleMotion = (e: DeviceMotionEvent) => {
    const acc = e.accelerationIncludingGravity;

    if (acc) {
      const x = acc.x ?? 0;
      const y = acc.y ?? 0;
      const z = acc.z ?? 0;

      // Convert raw accelerometer data to beta and gamma angles (in degrees)
      const rawBeta = Math.atan2(x, Math.sqrt(y * y + z * z)) * (180 / Math.PI);
      const rawGamma =
        Math.atan2(y, Math.sqrt(x * x + z * z)) * (180 / Math.PI);

      // Add current values to history for smoothing
      motionBetaHistory.push(rawBeta);
      motionGammaHistory.push(rawGamma);

      // Limit history size to last 5 values
      if (motionBetaHistory.length > 5) motionBetaHistory.shift();
      if (motionGammaHistory.length > 5) motionGammaHistory.shift();

      // Calculate smoothed average values
      const smoothedBeta =
        motionBetaHistory.reduce((sum, val) => sum + val, 0) /
        motionBetaHistory.length;
      const smoothedGamma =
        motionGammaHistory.reduce((sum, val) => sum + val, 0) /
        motionGammaHistory.length;

      // Minimum threshold to reduce noise
      const sensitivity = 2;

      // Only trigger if movement exceeds sensitivity threshold
      if (
        Math.abs(smoothedBeta) > sensitivity ||
        Math.abs(smoothedGamma) > sensitivity
      ) {
        // Create a fake DeviceOrientationEvent using smoothed data
        const fakeOrientationEvent = {
          beta: smoothedBeta,
          gamma: smoothedGamma,
          alpha: 0,
        } as DeviceOrientationEvent;

        // Pass it to the main orientation handler
        handleOrientation(fakeOrientationEvent);
      }
    }
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("deviceorientation", handleOrientation);
      window.removeEventListener("devicemotion", handleMotion);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box>
        {step?.number === MODAL_STEPS.SCAN_1.number && (
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              textAlign: "center",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "18px",
                fontWeight: 600,
                lineHeight: "22px",
                maxWidth: "180px",
                fontFamily: "Inter",
              }}
            >
              {t("steps.scan_1")}
            </span>
            <button
              type="button"
              onClick={() => {
                requestPermission();
                nextStep();
              }}
              style={{
                background: isIntersport
                  ? activeStylesContinue?.backgroundColor
                  : "#000",
                borderRadius: "50%",
                width: "47px",
                height: "47px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={Flèche}
                alt="arrow"
                style={{
                  width: "37px",
                  height: "37px",
                }}
              />
            </button>
          </Box>
        )}
        {step?.number === MODAL_STEPS.SCAN_2.number && (
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              textAlign: "center",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "18px",
                fontWeight: 600,
                lineHeight: "22px",
                maxWidth: "200px",
                fontFamily: "Inter",
              }}
            >
              {t("steps.scan_2")}
            </span>
          </Box>
        )}
      </Box>
    </>
  );
};

export default Camera;
